import type { StationeryProduct } from '@shared/utils/print-price/types';
import { StationeryPrintOrderVendorEnum } from '@graphql/generated-base';

/**
 * Synthetic "Basic Digital" (free ecard) product used to surface the free
 * digital option on templates that have no purchasable basic-digital product of
 * their own:
 * - PDP: appended to a paper template's products when it also has a Joy digital
 *   design (`hasBasicDigital`), and used as the sole product for digital-only
 *   themes whose paper template is disabled/absent.
 * - Gallery: injected into digital-only design tiles so they render a
 *   "Free Digital" price through the normal product pricing pipeline.
 *
 * `productType: 'digital'` maps to the `digital` entry in `useProductTypes`
 * ("Basic Digital" / "Free Digital") and is classified by `isDigitalProduct`.
 */
export const FREE_DIGITAL_DUMMY_PRODUCT: StationeryProduct = {
  id: 'FREEDIGITALDUMMY',
  priceConfigId: '',
  vendor: StationeryPrintOrderVendorEnum.joy_digital,
  productType: 'digital',
  vendorBaseSKU: '',
  priceCurrency: '',
  attributes: [],
  pricing: {
    __typename: 'StationeryFlatFeePricing',
    priceInMinorUnits: 0,
  },
  variantGroups: [],
};

/**
 * Synthetic "Basic Digital V2" (free) product. Like the dummy above it exists only
 * to surface a free option through the normal product pricing pipeline, but the
 * experience behind it is the premium-digital one: it creates a real stationery
 * draft from the paper template's `themeJSON` and opens the digital customizer,
 * rather than redirecting to the legacy ecard editor.
 *
 * Gated on the `printEnableBasicDigitalV2` flag plus a card_service themeId
 * allowlist — see `useIsBasicDigitalV2Enabled`.
 */
export const BASIC_DIGITAL_V2_DUMMY_PRODUCT: StationeryProduct = {
  ...FREE_DIGITAL_DUMMY_PRODUCT,
  id: 'BASICDIGITALV2DUMMY',
  productType: 'basicDigitalV2',
};
